import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import styles from './HierarchicalFacetValueBrowser.module.css';
import { ccConfig } from '../CC/CC'; 
import { generateQuery } from '../CC/utils'; 
import { useCCContext } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import GenericCheckbox from '../GenericComponents/GenericCheckbox';
import * as ccUtils from '../CC/utils';

interface FacetProps {
  hierarchicalFacetName: string;
  CN?: string;
  onSelect?: (values: string[]) => void; // Updated to return array of CN strings
  showSelectedAbove?: boolean;
  isOpen: boolean;
  onClose?: () => void;
}

interface FacetDataItem {
  value: string; // CN
  term: string;  // Actual term
  count: string; // Assuming count is provided or set to a default
}

interface SelectedFacet {
  CN: string;
  term: string;
}

const HierarchicalFacetValueBrowser: React.FC<FacetProps> = ({
  hierarchicalFacetName,
  CN,
  onSelect = undefined,
  showSelectedAbove = true,
  isOpen = false,
  onClose = () => {}
}) => {

  const { t } = useTranslation();  
  const field = "/Record/ThesTerm/TermCN";
  const instanceRef = useRef({});
  const { cc, setCc, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData } = useCCContext();
  
  const [userInput, setUserInput] = useState('');
  const [searchTrigger, setSearchTrigger] = useState(0); // New state to trigger search
  const [selectedValues, setSelectedValues] = useState<SelectedFacet[]>(cc.hierarchicalFacets[hierarchicalFacetName]?.map((item: any) => ({ CN: item.CN, term: item.term })) || []);
  const [facetData, setFacetData] = useState<FacetDataItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const observer = useRef<IntersectionObserver | null>(null);
  const [facetCount, setFacetCount] = useState(0);
  const facetRootRef = useRef<HTMLDivElement>(null);
  
  let hierarchicalFacetLabel = "";
  if (ccConfig.hierarchicalFacets[hierarchicalFacetName]) {
    hierarchicalFacetLabel = ccConfig.hierarchicalFacets[hierarchicalFacetName].label;
  }
  
  const lastElementRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMore]);

  const fetchData = async (currentPage: number) => {
    const pageSize = 100;
    const query = generateQuery(cc, { excludeFacet: hierarchicalFacetName }); 
    console.log(hierarchicalFacetName+" "+CN);
    const postData = {
      action: "get",
      command: "search",
      query: `and(${query};and(/Record/Thes/CN="${CN}";/Record/Thes/Term=${userInput}*))`,
      range: `${(currentPage - 1) * pageSize + 1}-${currentPage * pageSize}`,
      sort: "/Record/Thes/Term",
      responseformat: "json"
    };

    try {
      const data = await ccUtils.postWithCache(process.env.REACT_APP_BASE_URL as string, postData, instanceRef);
      
      // Adapted to process records from backend
      const newData: FacetDataItem[] = Array.isArray(data.records.record)
        ? data.records.record.map((record: any) => ({
            value: record.data.Record.Thes.CN,
            term: record.data.Record.Thes.Term,
            count: "1" // Set to "1" or derive from data if available
          }))
        : [{
            value: data.records.record.data.Record.Thes.CN,
            term: data.records.record.data.Record.Thes.Term,
            count: "1"
          }];

      setFacetData(prev => {
        const updatedData = [...prev];
        newData.forEach(item => {
          if (!updatedData.find(i => i.value === item.value)) {
            updatedData.push(item);
          }
        });
        return updatedData;
      });

      // Determine if more data is available
      setHasMore(newData.length === pageSize);
      setFacetCount(parseInt(data.request.count, 10));
    } catch (error) {
      setFacetCount(0);
      console.error(error);
    }
  };  

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setUserInput(e.target.value);
    // Search will be triggered when the search button is clicked
  };

  const clearInput = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setUserInput('');
  };

  const handleCheckboxChange = (checked: boolean, value: string) => {
    const termValue = facetData.find(item => item.value === value)?.term || "";

    if (viewMode === "homepage") {
      setViewMode('list');
    }
    
    if (checked) {
      // If the checkbox is checked, add the CN and term to the list of selected hierarchical facets
      const updatedHierarchicalFacets = cc.hierarchicalFacets[hierarchicalFacetName]
        ? [...cc.hierarchicalFacets[hierarchicalFacetName], { CN: value, term: termValue }]
        : [{ CN: value, term: termValue }];

      setCc((prev: typeof cc) => ({
        ...prev,
        hierarchicalFacets: {
          ...prev.hierarchicalFacets,
          [hierarchicalFacetName]: updatedHierarchicalFacets
        }
      }));
      
      // Update local selectedValues state
      setSelectedValues(prev => [...prev, { CN: value, term: termValue }]);
    } else {
      // If the checkbox is unchecked, remove the CN from the list of selected hierarchical facets
      const updatedHierarchicalFacets = cc.hierarchicalFacets[hierarchicalFacetName].filter((hf: SelectedFacet) => hf.CN !== value);

      setCc((prev: typeof cc) => ({
        ...prev,
        hierarchicalFacets: {
          ...prev.hierarchicalFacets,
          [hierarchicalFacetName]: updatedHierarchicalFacets
        }
      }));
      
      // Update local selectedValues state
      setSelectedValues(prev => prev.filter(item => item.CN !== value));
    }
  };

  // Effect to fetch data when search is triggered or page changes
  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTrigger, page]);

  useEffect(() => {
    // Initialize selectedValues from context
    if (cc.hierarchicalFacets[hierarchicalFacetName]) {
      setSelectedValues(cc.hierarchicalFacets[hierarchicalFacetName].map((item: any) => ({ CN: item.CN, term: item.term })));
    } else {
      setSelectedValues([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cc.hierarchicalFacets, hierarchicalFacetName]);

  const handleSearch = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    setFacetData([]);
    setPage(1);
    setSearchTrigger(prev => prev + 1); // Trigger the useEffect to fetch data

    setCc(prev => ({
      ...prev,
      hierarchicalFacets: {
        ...prev.hierarchicalFacets,
        [hierarchicalFacetName]: selectedValues // Already updated via handleCheckboxChange
      }
    }));

    if(onSelect) {
      onSelect(selectedValues.map(value => value.CN));
    }    

    onClose();
  };

  const handleCancel = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    onClose();
  };  

  return (
    isOpen ? (
      <div className={styles.HierarchicalFacetValueBrowser}>
        <div className={styles.popupoverlay}></div>
        <div className={`${styles.popup} parentModalClass`}>

          <div className={styles.container}>

            <div className={styles.facetName}>
              <span className={styles.facetNameLabel}>
                {t(hierarchicalFacetLabel)}
              </span>
              <span className={styles.facetNameCount}>
                ({facetCount} {facetCount !== 1 ? t("facet_values") : t("facet_value")})
              </span>
            </div>

            <div className={styles.facetContainer} ref={facetRootRef}>
              <div className={styles.header}>
                <div className={styles.inputContainer}>
                  <input
                    className={styles.mask}
                    type="text"
                    value={userInput}
                    placeholder={t("facet_filter")}
                    onChange={handleInput}
                  />
                  <GenericIconButton
                    icon='light/times.svg'
                    width='15px'
                    height='15px'
                    fill='#383838'
                    hoverBackgroundColor='#f0f0f0'
                    onClick={clearInput}
                  />
                  <button
                    className={genericStyles.GenericButton}
                    onClick={() => {
                      setFacetData([]);
                      setPage(1);
                      setSearchTrigger(prev => prev + 1);
                    }}
                    style={{ marginLeft: '8px' }}
                  >
                    {t("search")}
                  </button>
                </div>
                {/* Removed sort buttons */}
              </div>
              <div className={styles.selectedItems}>
                {showSelectedAbove && selectedValues.length > 0 && (
                  <div className={styles.selectedList}>
                    {selectedValues.map((facet) => (
                      <div key={facet.CN} className={styles.selectedItem}>
                        <GenericCheckbox
                          id={`facet_${field}_${facet.CN}`}
                          name={`facet_${field}_${facet.CN}`}
                          value={facet.CN}
                          checked={true}
                          onChange={(checked: boolean) => handleCheckboxChange(checked, facet.CN)}
                          label={`${facet.term}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.content}>
                {
                  facetData.map((item, index) => {
                    return (
                      <div key={item.value} ref={index === facetData.length - 1 ? lastElementRef : null}>
                        <GenericCheckbox
                          id={`facet_${field}_${item.value}`}
                          name={`facet_${field}_${item.value}`}
                          value={item.value}
                          checked={selectedValues.some(selectedItem => selectedItem.CN === item.value)}
                          onChange={(checked: boolean) => handleCheckboxChange(checked, item.value)}
                          label={item.term} // Display only the term
                        />
                      </div>
                    );
                  })
                }
              </div>

            </div>

            <div className={styles.footer}>
              <button className={genericStyles.GenericButton} onClick={handleSearch}>
                {t("browse_apply")}
              </button>
              <button className={genericStyles.GenericButton} onClick={handleCancel}>
                {t("browse_cancel")}
              </button>
            </div>

          </div>

        </div>
      </div>
    ) : null
  )

}

export default HierarchicalFacetValueBrowser;
